import { authFetch } from './auth-fetch.js';

export type AuthFetchWithExchangeRetryParams = {
  /** Magic-link segment from the scheduling URL; used to mint a new JWT when the session JWT expires. */
  magicLinkToken: string | undefined;
  setJwt: (jwt: string) => void;
  apiBase: string;
  jwt: string;
  url: string;
  init?: RequestInit;
};

function parseExchangeJwt(data: unknown): string | null {
  if (data && typeof data === 'object' && 'jwt' in data) {
    const j = data.jwt;
    if (typeof j === 'string' && j.length > 0) {
      return j;
    }
  }
  return null;
}

/**
 * Calls {@link authFetch}; on **401** (expired session JWT), re-posts the magic link to
 * `/link/exchange`, updates JWT via `setJwt`, and retries the request once.
 *
 * @remarks The consult-scheduling Bearer JWT is short-lived; users who keep the tab open past
 * expiry would otherwise see `Unauthorized` on webinar list/register or consultants load.
 *
 * ## 401 ONLY, and that is VERIFIED against the service rather than assumed
 *
 * A review raised that NestJS `AuthGuard` variants commonly answer 403 for an expired or invalid
 * token, in which case a 401-only trigger would leave every re-exchange path silently dead and the
 * member would see "Unable to load sessions" after roughly thirty minutes of dwell. The fixtures in
 * `session-reexchange.spec.tsx` all return 401, so the tests could not tell a correct contract from
 * an assumed one.
 *
 * The service does not use an `AuthGuard` variant. Its `JwtAuthGuard` is hand-written and throws a
 * bare `UnauthorizedException()` at every failure site, with no `ForbiddenException` anywhere in it,
 * and Nest maps that to 401. That guard is NOT in this repository: it arrives with the
 * consult-scheduling service in PR #1050, as
 * `services/consult-scheduling/src/interface/http/guards/jwt-auth.guard.ts`.
 *
 * So 401 is the whole contract, and widening this to accept 403 would buy nothing: the only 403s
 * this app can see come from CloudFront or the WAF, and re-exchanging a magic link does not make a
 * blocked request pass. If that guard ever grows a `ForbiddenException`, this is the line to change.
 */
export async function authFetchWithExchangeRetry(
  p: AuthFetchWithExchangeRetryParams,
): Promise<Response> {
  const { magicLinkToken, setJwt, apiBase, jwt, url, init } = p;
  const trimmedMagic = magicLinkToken?.trim();
  const first = await authFetch(jwt, url, init);
  if (first.status !== 401 || !trimmedMagic) {
    return first;
  }

  const exchangeRes = await fetch(`${apiBase}/link/exchange`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token: trimmedMagic }),
    ...(init?.signal ? { signal: init.signal } : {}),
  });

  if (!exchangeRes.ok) {
    return first;
  }

  let body: unknown;
  try {
    body = await exchangeRes.json();
  } catch {
    return first;
  }

  const newJwt = parseExchangeJwt(body);
  if (!newJwt) {
    return first;
  }

  setJwt(newJwt);
  return authFetch(newJwt, url, init);
}
